import type { CeqaBudget, CeqaRunSummary, RadarRow } from '../../lib/api';

/**
 * The new-development lane's pure helpers (spec 9.2-9.5): no React, no fetch, so every rule is table-tested
 * instead of reasoned about. The page and the row both read them - one definition each.
 */

/**
 * ⭐ HIS ORDER, D8 (2026-09-27): APPROVED FIRST. The chip names the STAGE of the latest filing - red is a
 *   project cleared to build (leasing soon), amber one in review (get in early), blue anything else.
 * ⛔ The backend bands in the same order (`ceqaStages.ts`); this map only NAMES the bands.
 */
export const DEVELOPMENT_BAND_LABEL: Record<'urgent' | 'prime' | 'early', string> = {
  urgent: 'Approved',
  prime: 'In review',
  early: 'Other filing',
};

export type DevelopmentOutcome = 'developer_research' | 'not_a_fit';

/** How a worked development row reads back - in the undo banner and the activity list. */
export const DEVELOPMENT_OUTCOME_LABEL: Record<DevelopmentOutcome, string> = {
  developer_research: 'Handed to research',
  not_a_fit: 'Not a fit',
};

/**
 * ⭐ D4, his words: "there's some nice buildings that are 5k sq ft and half an acre truck yard is still pretty
 *   good." The DEFAULT view, never a wall: a project STATED to be under both floors is hidden and COUNTED by the
 *   control that hides it.
 */
export const SIZE_FLOOR = { buildingSf: 5000, siteAcres: 0.4 } as const;

const isNum = (n: number | null | undefined): n is number => typeof n === 'number' && Number.isFinite(n);

export const hasStatedSize = (r: RadarRow): boolean => isNum(r.building_sf) || isNum(r.site_acres);

/** A size that is STATED and under its floor. ⛔ An unstated size is under no floor - nothing proves it small. */
const statedUnder = (n: number | null | undefined, floor: number): boolean => isNum(n) && n < floor;

/**
 * ⛔⛔ THE SIZE FLOOR RULING (controller, 2026-09-29 - Joseph to confirm): hidden by the default view ONLY when EVERY size
 *    a row could pass on is STATED and under its floor. D4 is "building >= 5,000 SF OR site >= 0.4 acres", so an
 *    unstated half could still pass: a small building with no site stated is SHOWN (a small building on a big yard is
 *    exactly the truck-yard target), and so is a small site with no building stated - each marked by `sizeLine`. A row
 *    with no size at all is SHOWN, marked "size not stated". A blank is a to-do, not a verdict (spec 9.4).
 */
export const isHiddenBySizeFilter = (r: RadarRow): boolean =>
  statedUnder(r.building_sf, SIZE_FLOOR.buildingSf) && statedUnder(r.site_acres, SIZE_FLOOR.siteAcres);

/**
 * A development project a person handed to research (plan decision 12). ⛔ It is GONE from the worklist like
 *   any worked row, and one COUNTED control away - the screen is the only place its developer contact can be
 *   added, and adding it is what clears the hand-off.
 */
export const isHandedToResearch = (r: RadarRow): boolean =>
  r.signal_type === 'new_development' && r.suppressed && r.mark?.outcome === 'developer_research';

const pad = (n: number) => String(n).padStart(2, '0');

/** His CALENDAR date as YYYY-MM-DD - never the UTC date, which turns over at 5 PM in California. */
export const localIsoDay = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * ⛔ RULING (I4): an INSTANT (an ISO timestamp - a save, a run's start or finish) as his calendar day, so nothing done
 *   after 5 PM in California reads as tomorrow. A bare date (a CEQA "Received" day) already names a day and is kept
 *   as it is: parsed, it would read as UTC midnight - the day before where he is. A value that is no date is shown
 *   as sent, never turned into one.
 */
export const localDayOf = (instant: string | null | undefined): string => {
  if (!instant) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(instant)) return instant;
  const t = Date.parse(instant);
  return Number.isFinite(t) ? localIsoDay(new Date(t)) : instant;
};

/**
 * "Just filed": the latest filing arrived within 14 days (spec 9.3). ⛔ Computed on SCREEN and never part of a
 *   mark - a band built from age would bring every worked row back 14 days later for no reason (C1).
 */
export const JUST_FILED_DAYS = 14;

export const justFiled = (r: RadarRow, today: Date): boolean => {
  if (!r.latest_received) return false;
  const days = (Date.parse(localIsoDay(today)) - Date.parse(r.latest_received)) / 86_400_000;
  return days >= 0 && days <= JUST_FILED_DAYS;
};

const sf = (n: number) => Math.round(n).toLocaleString('en-US');
const acres = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });

/**
 * The size a row is SHOWN for want of (the size floor ruling): a stated size under its floor with the other size not
 * stated. Named on the row, so a project on the list only because a number is missing reads as the to-do it is.
 */
const missingSize = (r: RadarRow): string | null => {
  if (isNum(r.building_sf) && r.building_sf < SIZE_FLOOR.buildingSf && !isNum(r.site_acres)) return 'site size not stated';
  if (isNum(r.site_acres) && r.site_acres < SIZE_FLOOR.siteAcres && !isNum(r.building_sf)) return 'building size not stated';
  return null;
};

/**
 * "1,830,729 SF · 78.3 acres", either one, or "size not stated" - with the missing size named when the row is shown
 * only for want of it ("4,999 SF · site size not stated"). ⛔ A missing number is never shown as a zero.
 */
export const sizeLine = (r: RadarRow): string => {
  const parts = [
    isNum(r.building_sf) ? `${sf(r.building_sf)} SF` : null,
    isNum(r.site_acres) ? `${acres(r.site_acres)} acres` : null,
    missingSize(r),
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'size not stated';
};

/** "Notice of determination 2026-08-20 · first filed 2025-09-29"; a project with one filing names it once. */
export const stageLine = (r: RadarRow): string => {
  const latest = [r.latest_label, r.latest_received].filter(Boolean).join(' ');
  if (!r.first_received || r.first_received === r.latest_received) return latest;
  return `${latest} · first filed ${r.first_received}`;
};

export const usd = (n: number): string => `$${n.toFixed(2)}`;

/**
 * A message as one sentence, ended ONCE. The server's words arrive with a full stop ("No connection - check your
 * signal and try again.") or without one ("Failed to load the CEQA reader status"), and a line built around them
 * must never read "..". Empty stays empty.
 */
export const asSentence = (s: string): string => {
  const t = s.trim();
  return !t || /[.!?]$/.test(t) ? t : `${t}.`;
};

/**
 * The server's outer bounds on each cap (`BUDGET_LIMITS` in aiBudget.ts), mirrored because the frontend cannot
 * import backend source (trap 20). `aiBudget.mirror.test.ts` fails when the two disagree.
 */
export const CAP_LIMITS = { cap_load_usd: 1000, cap_night_usd: 100, cap_30d_usd: 2000 } as const;

export type CapKey = keyof typeof CAP_LIMITS;

/**
 * ⛔ The nightly cap is PER RUN: every nightly run - one started by hand included - gets a fresh one, and the
 *   30-day cap bounds them all (ceqaRun `readCandidates` checks a run's own spend against it). The label says
 *   so, because "Nightly cap" alone reads as a cap on the night.
 */
export const CAP_LABEL: Record<CapKey, string> = {
  cap_load_usd: 'History load cap',
  cap_night_usd: 'Nightly cap per run',
  cap_30d_usd: '30-day cap',
};

/**
 * A cap as he typed it, in dollars - or the reason it is refused, by the SERVER's rule (`validateBudgetPatch`):
 * a dollar amount from 0 to the cap's limit, rounded to cents. A "$" and thousands commas are forgiven.
 */
export const parseCapInput = (key: CapKey, text: string): { ok: true; value: number } | { ok: false; error: string } => {
  const t = text.trim().replace(/^\$/, '').replace(/,/g, '');
  const n = Number(t);
  if (!t || !Number.isFinite(n) || n < 0 || n > CAP_LIMITS[key]) {
    return { ok: false, error: `${CAP_LABEL[key]} must be a dollar amount from 0 to ${CAP_LIMITS[key]}` };
  }
  return { ok: true, value: Math.round(n * 100) / 100 };
};

/**
 * What a run recorded, as ONE sentence - or that nothing was recorded. The server's words keep their own full stop
 * (an `UnknownModelPriceError` ends with one) and gain one only when they have none: never "..".
 */
const detailOf = (run: CeqaRunSummary, fallback = 'no detail was recorded'): string =>
  asSentence(run.pause_detail?.trim() || fallback);

/**
 * The history load's state in words (spec 7.2, 7.3). ⛔ A pause is not a wall: it says WHICH cap, what was spent,
 * HOW MANY WERE READ and how many wait (the Global Constraint - the cap's own words carry the spend, the tally the
 * rest). The checkpoint says what month one cost - and whether the load cap would stop the rest partway, so a
 * raise can come first.
 */
export const loadLine = (run: CeqaRunSummary | null, budget: CeqaBudget): string => {
  if (!run) return 'The 12-month history load has not run yet.';
  const sofar = `${run.read_count} read, ${usd(run.spend_usd)} spent`;
  if (run.status === 'running' && run.stalled) {
    return `The history load has gone quiet for 30 minutes and may have stopped (${sofar}). Continue picks it up where it left off.`;
  }
  if (run.status === 'running') {
    const done = run.months.filter((m) => m.status === 'done').length;
    return `The history load is running: ${done} of ${run.months.length} months read, ${sofar}.`;
  }
  if (run.status === 'done') {
    return `The history load finished ${localDayOf(run.finished_at)}: ${sofar}, ${run.unreadable} could not be read.`;
  }
  if (run.status === 'failed') return `The history load failed: ${detailOf(run)}`;
  const wait = `${run.waiting} ${run.waiting === 1 ? 'filing waits' : 'filings wait'}`;
  const tally = `${run.read_count} read so far; ${wait}`;
  switch (run.pause_reason) {
    case 'checkpoint': {
      const summary = run.report?.summary ?? run.pause_detail ?? '';
      const projected = run.spend_usd + (run.report?.projectedRemaining ?? 0);
      const warn = projected > budget.cap_load_usd
        ? ` At that rate the history load cap (${usd(budget.cap_load_usd)}) will pause it again before the end - raise it first to read straight through.`
        : '';
      return `Paused at the first-month checkpoint. ${summary}${warn} Continuing is your yes to the other 11 months.`;
    }
    case 'cap_run':
      return `Paused at the history load cap: ${detailOf(run)} ${tally}.`;
    case 'cap_30d':
      return `Paused at the 30-day cap: ${detailOf(run)} ${tally}.`;
    case 'api_error':
      return `Paused because the AI service failed: ${detailOf(run)} ${tally}.`;
    case 'unknown_price':
      // Where a cap figure that could not be checked lands too (ceqaRun) - no raise clears it, so none is offered.
      return `Paused: ${detailOf(run, 'a model with no recorded price')} ${tally}.`;
    default:
      return `Paused. ${tally}.`;
  }
};

/**
 * ⛔ F4 (the final review): what Continue asks at the first-month checkpoint, before anything is sent. That press IS his
 *   yes to the other 11 months (spec 7.3) and lets the load read up to the history load cap - many times the ~$1 that
 *   Start names - so, like Start, it says what it authorises, in figures: month one's own summary and the cap, with what
 *   month one has already spent of it (the load cap counts the whole run).
 */
export const checkpointConfirm = (run: CeqaRunSummary, capLoadUsd: number): string => [
  'Continue past the first-month checkpoint?',
  asSentence(run.report?.summary ?? run.pause_detail ?? ''),
  `This is your yes to the other 11 months: the load reads on until it finishes or its spend reaches the history load cap of ${usd(capLoadUsd)} (month one spent ${usd(run.spend_usd)} of it).`,
].filter(Boolean).join(' ');

/**
 * The nightly in words. ⛔ It is OPT-IN (plan decision 2): off until the Railway switch is set.
 * ⛔⛔ `nightlyEnabled` is the SWITCH (`ENABLE_CEQANET_CRON`), never proof a job is scheduled - the scheduler refuses an
 *    invalid `CEQANET_CRON` while the switch is on. So "on" names the switch, and says when no run is recorded yet.
 * ⛔ Each run is worded as what it IS: reading now, gone quiet (the server's own `isStalled` - marked running and silent
 *   30 minutes; it blocks no later run), paused, failed or finished. A run still reading never reads as a finished one.
 */
export const nightlyLine = (run: CeqaRunSummary | null, enabled: boolean): string => {
  const state = enabled
    ? 'The nightly switch is on (ENABLE_CEQANET_CRON=true).'
    : 'Nightly reading is off - turning it on is a Railway setting (ENABLE_CEQANET_CRON=true).';
  if (!run) return enabled ? `${state} No nightly run is recorded yet.` : state;
  const day = localDayOf(run.started_at);
  const sofar = `${run.read_count} read, ${usd(run.spend_usd)}`;
  if (run.status === 'running' && run.stalled) {
    return `${state} The run that started ${day} has gone quiet for 30 minutes and may have stopped (${sofar} spent) - it does not hold up the next night's run.`;
  }
  if (run.status === 'running') return `${state} The run that started ${day} is reading now: ${sofar} so far.`;
  if (run.status === 'failed') return `${state} The last run (${day}) failed: ${detailOf(run)}`;
  if (run.status === 'paused') {
    const waits = `${run.waiting} ${run.waiting === 1 ? 'waits' : 'wait'}`;
    return `${state} The last run (${day}) paused: ${detailOf(run)} ${run.read_count} read; ${waits} for the next night.`;
  }
  return `${state} The last run (${day}): ${sofar}.`;
};
